// Tier 2 #4 — Preferences (v1: language only).
//
// Per the design notes (CLAUDE.local.md) and the parity-plan Tier 2
// entry, the Preferences surface is reached from the About hamburger
// menu, not the main nav. v1 ships a single setting: preferred
// language for label resolution. Theme / dark-mode / default-graph
// are deferred to v1.x — see project_v3_ui_term_migration.md and
// related memos.

import { useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listGitSources,
  type GitSource,
  queryLogGrant,
  setQueryLogGrant,
  type ApiFailure,
} from '@/lib/api';
import {
  getPreferredLanguage,
  setPreferredLanguage,
  getThemePreference,
  getVizLabelLimit,
  setThemePreference,
  setVizLabelLimit,
  SUPPORTED_LANGUAGES,
  type ThemePreference,
} from '@/lib/preferences';
import { canExportStore, canImportData, canManageSettings, useMe } from '@/lib/useMe';
import {
  canRememberFolder,
  chooseDefaultSnapshotFolder,
  clearDefaultSnapshotFolder,
  defaultSnapshotFolder,
} from '@/lib/saveFile';
import { deleteLogo, setWelcome, uploadLogo, useAdminBranding } from '@/lib/useAdmin';
import { cn } from '@/lib/utils';
import { messageClass } from '@/components/ui/Message';
import { buttonVariants } from '@/components/ui/Button';

export function PreferencesPage() {
  const queryClient = useQueryClient();
  const [lang, setLang] = useState<string | null>(() => getPreferredLanguage());
  const [theme, setTheme] = useState<ThemePreference>(() => getThemePreference());
  const [labelLimit, setLabelLimit] = useState<number>(() => getVizLabelLimit());
  const [saved, setSaved] = useState(false);

  // Auto-dismiss the "saved" confirmation after a couple seconds so
  // the page doesn't accumulate stale confirmation banners.
  useEffect(() => {
    if (!saved) return;
    const t = setTimeout(() => setSaved(false), 2000);
    return () => clearTimeout(t);
  }, [saved]);

  function handleSave(next: string | null) {
    setLang(next);
    setPreferredLanguage(next);
    // Invalidate cached query data — label resolution depends on the
    // Accept-Language header which apiFetch reads from localStorage
    // on each call. Cached results from the previous language are
    // now stale.
    void queryClient.invalidateQueries();
    setSaved(true);
  }

  function handleThemeSave(next: ThemePreference) {
    setTheme(next);
    setThemePreference(next);
    setSaved(true);
  }

  return (
    <div className="container mx-auto max-w-2xl py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-tres-text-primary">Preferences</h1>
        <p className="mt-1 text-sm text-tres-text-secondary">
          Settings stored in your browser. They affect what you see on this device only.
        </p>
      </header>

      <section className="rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-tres-text-primary">Preferred language</h2>
        <p className="mt-1 text-xs text-tres-text-secondary">
          When an entity has labels in multiple languages, TReS will show the version in your
          preferred language. Falls back to English, then to any available label, then to the
          URI's local name.
        </p>

        <div className="mt-4 grid gap-2">
          <label className="flex items-center gap-3">
            <input
              type="radio"
              name="language"
              checked={lang === null}
              onChange={() => handleSave(null)}
              className="h-4 w-4 accent-tres-action"
            />
            <span className="text-sm">
              <span className="font-medium text-tres-text-primary">No preference</span>
              <span className="ml-2 text-xs text-tres-text-secondary">
                Use English-first default
              </span>
            </span>
          </label>
          {SUPPORTED_LANGUAGES.map((l) => (
            <label key={l.code} className="flex items-center gap-3">
              <input
                type="radio"
                name="language"
                checked={lang === l.code}
                onChange={() => handleSave(l.code)}
                className="h-4 w-4 accent-tres-action"
              />
              <span className="text-sm">
                <span className="font-medium text-tres-text-primary">{l.label}</span>
                <span className="ml-2 font-mono text-xs text-tres-text-secondary">{l.code}</span>
              </span>
            </label>
          ))}
        </div>

        <div
          className={cn(
            'mt-4 h-5 text-xs',
            saved ? 'text-tres-success' : 'text-transparent',
          )}
        >
          Saved.
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-tres-text-primary">Visualization labels</h2>
        <p className="mt-1 text-sm text-tres-text-secondary">
          Past a certain number of nodes the labels on the Viz canvas stop being readable at any
          zoom that shows the whole graph, so above this count nodes draw as bare boxes and the
          label of a selected node still shows. Set 0 to keep labels at every size. Labels are
          always withdrawn when zoomed far out.
        </p>
        <label className="mt-4 flex items-center gap-3 text-sm text-tres-text-primary">
          <span>Withdraw labels above</span>
          <input
            type="number"
            min={0}
            step={10}
            value={labelLimit}
            onChange={(e) => {
              const next = Math.max(0, Math.floor(Number(e.target.value) || 0));
              setLabelLimit(next);
              setVizLabelLimit(next);
            }}
            className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm"
          />
          <span>nodes</span>
        </label>
      </section>

      <section className="mt-6 rounded-lg border border-border bg-card p-5">
        <h2 className="text-sm font-semibold text-tres-text-primary">Theme</h2>
        <p className="mt-1 text-xs text-tres-text-secondary">
          Choose a light or dark palette, or follow the operating system's setting. Applies
          to this browser only.
        </p>

        <div className="mt-4 grid gap-2">
          {(
            [
              { code: 'system' as const, label: 'Match system', hint: 'Follow OS appearance' },
              { code: 'light' as const, label: 'Light', hint: null },
              { code: 'dark' as const, label: 'Dark', hint: null },
            ]
          ).map((opt) => (
            <label key={opt.code} className="flex items-center gap-3">
              <input
                type="radio"
                name="theme"
                checked={theme === opt.code}
                onChange={() => handleThemeSave(opt.code)}
                className="h-4 w-4 accent-tres-action"
              />
              <span className="text-sm">
                <span className="font-medium text-tres-text-primary">{opt.label}</span>
                {opt.hint && (
                  <span className="ml-2 text-xs text-tres-text-secondary">{opt.hint}</span>
                )}
              </span>
            </label>
          ))}
        </div>
      </section>

      <SnapshotFolderSection />

      <QueryLogAccessSection />

      <GitSourcesSection />
      <CustomizeSection />
    </div>
  );
}

// Where a database snapshot is saved unless another place is chosen when it is
// taken. A browser keeps a folder only where it offers file system access
// (Chrome, Edge); elsewhere every file goes to its Downloads folder.
function SnapshotFolderSection() {
  const me = useMe();
  const [folder, setFolder] = useState<FileSystemDirectoryHandle | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => {
    void defaultSnapshotFolder().then(setFolder);
  }, []);
  if (!canExportStore(me.data)) return null;

  async function choose() {
    const chosen = await chooseDefaultSnapshotFolder();
    if (chosen) {
      setFolder(chosen);
      setMessage(`Snapshots now save to the folder ${chosen.name}.`);
    } else {
      setMessage('No folder was chosen; nothing changed.');
    }
  }

  async function clear() {
    await clearDefaultSnapshotFolder();
    setFolder(null);
    setMessage('Cleared: a snapshot now asks where to save it.');
  }

  return (
    <section className="mt-6 rounded-lg border border-border bg-card p-5">
      <h2 className="text-sm font-semibold text-tres-text-primary">Snapshot folder</h2>
      <p className="mt-1 text-xs text-tres-text-secondary">
        Where a database snapshot is saved unless you choose another place when you take it.
        Applies to this browser only.
      </p>
      {canRememberFolder() ? (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
          <span>{folder ? `The folder ${folder.name}` : 'None set: a snapshot asks where to save it.'}</span>
          <button type="button" className={buttonVariants({ variant: 'secondary', size: 'sm' })} onClick={() => void choose()}>
            {folder ? 'Choose another folder…' : 'Choose folder…'}
          </button>
          {folder && (
            <button type="button" className={buttonVariants({ variant: 'secondary', size: 'sm' })} onClick={() => void clear()}>
              Clear
            </button>
          )}
        </div>
      ) : (
        <p className="mt-3 text-sm">This browser saves every file to its Downloads folder; a default folder needs Chrome or Edge.</p>
      )}
      {message && <div className={cn('mt-3', messageClass('outcome', 'compact'))}>{message}</div>}
    </section>
  );
}

// The Owner's grant over the query log (6.4.3/6.4.4, ruled 2026-08-13):
// vendor access to the query bodies is an affirmative act — opt-in, plainly
// labelled, revocable, never a default. Only the Owner sees the switch;
// Admins see the state on the Admin page's Query log tab instead.
function QueryLogAccessSection() {
  const auth = useAuth();
  const me = useMe();
  const qc = useQueryClient();
  const token = auth.user?.id_token;
  const isOwner = me.data?.role === 'Owner';

  const grant = useQuery({
    queryKey: ['query-log-grant'],
    enabled: isOwner && !!token,
    queryFn: async () => {
      const r = await queryLogGrant(token!);
      if (!r.ok) throw new Error(r.message);
      return r.data;
    },
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!isOwner) return null;

  const enabled = grant.data?.enabled ?? false;
  async function flip() {
    if (!token) return;
    setBusy(true);
    setError(null);
    const r = await setQueryLogGrant(!enabled, token);
    setBusy(false);
    if (r.ok) {
      void qc.invalidateQueries({ queryKey: ['query-log-grant'] });
    } else {
      setError(r.message);
    }
  }

  return (
    <section className="mt-6 rounded-lg border border-border bg-card p-5">
      <h2 className="text-base font-semibold text-tres-text-primary">Query log access</h2>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        The query log holds the text of every query and question on this site. You always see
        it (Admin → Query log). This switch decides whether Admin seats — including your
        provider&rsquo;s support seat — can read it too. It is off unless you turn it on, and
        you can turn it off at any time.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          onClick={() => void flip()}
          disabled={busy || grant.isLoading}
          className={cn(
            'rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50',
            enabled
              ? 'border border-border text-tres-text-primary hover:bg-muted'
              : 'bg-tres-action text-white hover:opacity-90',
          )}
        >
          {enabled ? 'Revoke Admin access' : 'Grant Admin access'}
        </button>
        <span className="text-xs text-muted-foreground">
          {grant.isLoading
            ? 'Checking…'
            : enabled
              ? `Granted${grant.data?.granted_at ? ` ${grant.data.granted_at}` : ''}`
              : 'Not granted — Admins cannot read the query log.'}
        </span>
      </div>
      {error && (
        <p className="mt-2 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/** Read-only view of the git-load sources the operator has configured.
 *  Surfaces what the Database Management page's "Load from Git" tab is reading
 *  from, so admins can see what's available without having to start a
 *  load. Editing requires the operator to update Terraform — there's
 *  no server-side preferences-persistence tier yet. */
function GitSourcesSection() {
  const auth = useAuth();
  const me = useMe();
  const token = auth.user?.id_token;
  const allowed = canImportData(me.data);
  const query = useQuery<GitSource[], Error>({
    queryKey: ['git-sources'],
    enabled: !!token && allowed,
    queryFn: async () => {
      if (!token) throw new Error('no token');
      const r = await listGitSources(token);
      if (!r.ok) throw new Error(r.message);
      return r.data.sources;
    },
  });

  // Hide entirely from users who can't import data — they'd never use
  // a source anyway, and the empty list isn't useful information for
  // them.
  if (!allowed) return null;

  const sources = query.data ?? [];

  return (
    <section className="mt-6 rounded-lg border border-border bg-card p-5">
      <h2 className="text-sm font-semibold text-tres-text-primary">Configured data sources</h2>
      <p className="mt-1 text-xs text-tres-text-secondary">
        Git repositories this instance can load data from directly, via the Database Management
        page's "Load from Git" tab. Sources are configured per instance, not per user.
      </p>
      {query.isLoading && (
        <p className="mt-3 text-xs text-tres-text-secondary">Loading…</p>
      )}
      {query.isError && (
        <p className="mt-3 text-xs text-destructive">
          Could not load sources: {query.error.message}
        </p>
      )}
      {!query.isLoading && !query.isError && sources.length === 0 && (
        <p className="mt-3 text-xs text-tres-text-secondary">
          No sources are configured for this instance. To add one,{' '}
          <a href="mailto:info@taurusystems.com" className="text-tres-action underline">
            contact support
          </a>{' '}
          with the repository URL — direct file upload on the Database Management page works
          without any configuration.
        </p>
      )}
      {sources.length > 0 && (
        <ul className="mt-3 space-y-2">
          {sources.map((s) => (
            <li
              key={s.id}
              className="rounded-md border border-border bg-background px-3 py-2 text-xs"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="font-medium text-tres-text-primary">{s.name}</span>
                <span
                  className={cn(
                    'rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                    s.authenticated
                      ? 'bg-tres-action/15 text-tres-action'
                      : 'bg-muted text-tres-text-secondary',
                  )}
                >
                  {s.authenticated ? 'Authenticated' : 'Public'}
                </span>
              </div>
              <div className="mt-1 font-mono text-tres-text-secondary break-all">
                {s.base_url}
              </div>
              <div className="mt-1 font-mono text-[10px] text-tres-text-secondary/70">
                id: {s.id}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

const MAX_LOGO_BYTES = 512 * 1024;

/** The Welcome page's logo and title for this site — instance-wide, so the
 *  section shows only to Admin and above; the API enforces ServerSettings. */
function CustomizeSection() {
  const auth = useAuth();
  const me = useMe();
  const token = auth.user?.id_token;
  const queryClient = useQueryClient();
  const allowed = canManageSettings(me.data);
  const branding = useAdminBranding(token, allowed);
  const [draft, setDraft] = useState<string | null>(null);
  const [draftTagline, setDraftTagline] = useState<string | null>(null);
  const [draftShow, setDraftShow] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const saved = branding.data?.welcome_title ?? '';
  const title = draft ?? saved;
  const savedTagline = branding.data?.welcome_tagline ?? '';
  const tagline = draftTagline ?? savedTagline;
  const savedShow = branding.data?.show_tagline ?? true;
  const showTagline = draftShow ?? savedShow;
  const dirty = title !== saved || tagline !== savedTagline || showTagline !== savedShow;
  const logo = branding.data?.logo ?? null;
  const logoSrc = logo
    ? `/api/branding/logo?v=${encodeURIComponent(logo.updated_at)}`
    : null;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'branding'] });
    void queryClient.invalidateQueries({ queryKey: ['branding'] });
  }

  function fail(r: ApiFailure) {
    setError(r.message);
  }

  async function saveWelcome() {
    if (!token) return;
    setBusy(true);
    setError(null);
    const r = await setWelcome(
      { welcome_title: title, welcome_tagline: tagline, show_tagline: showTagline },
      token,
    );
    setBusy(false);
    if (r.ok) {
      setDraft(null);
      setDraftTagline(null);
      setDraftShow(null);
      refresh();
    } else fail(r);
  }

  async function upload(file: File) {
    if (!token) return;
    if (file.size > MAX_LOGO_BYTES) {
      setError(
        `${file.name} is ${Math.round(file.size / 1024)} KB; the limit is ${MAX_LOGO_BYTES / 1024} KB.`,
      );
      return;
    }
    setBusy(true);
    setError(null);
    const r = await uploadLogo(file, token);
    setBusy(false);
    if (r.ok) refresh();
    else fail(r);
  }

  async function removeLogo() {
    if (!token) return;
    setBusy(true);
    setError(null);
    const r = await deleteLogo(token);
    setBusy(false);
    if (r.ok) refresh();
    else fail(r);
  }

  if (!allowed) return null;

  return (
    <section className='mt-6 rounded-lg border border-border bg-card p-5'>
      <h2 className='text-sm font-semibold text-tres-text-primary'>Custom Welcome Page</h2>
      <p className='mt-1 text-xs text-tres-text-secondary'>
        Instance-wide, Admin only: what everyone sees on the Welcome page. Your
        logo replaces the TReS logo there, the title replaces "Welcome to TReS",
        and the tagline can be yours or hidden; the header keeps the TReS logo
        and name. Changes show on the next page load.
      </p>
      {error && (
        <div className={cn(messageClass('warning', 'normal'), 'mt-3')}>
          {error}
        </div>
      )}

      <div className='mt-4 rounded-md border border-border bg-background p-4'>
        <div className='text-sm font-medium text-tres-text-primary'>Welcome title</div>
        <p className='mt-1 text-xs text-tres-text-secondary'>
          For example 'TReS for <span className='italic'>your programme</span>'. Leave
          blank for 'Welcome to TReS'. Up to 80 characters.
        </p>
        <div className='mt-3 flex flex-wrap items-center gap-2'>
          <input
            type='text'
            value={title}
            maxLength={80}
            placeholder='Welcome to TReS'
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy || branding.isLoading}
            className='w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50'
          />
        </div>
        <div className='mt-4 text-sm font-medium text-tres-text-primary'>Tagline</div>
        <p className='mt-1 text-xs text-tres-text-secondary'>
          The line under the title. Leave blank for "LLM Grounding Platform", or untick to show
          no tagline. Up to 80 characters.
        </p>
        <div className='mt-3 flex flex-wrap items-center gap-3'>
          <input
            type='text'
            value={tagline}
            maxLength={80}
            placeholder='LLM Grounding Platform'
            onChange={(e) => setDraftTagline(e.target.value)}
            disabled={busy || branding.isLoading || !showTagline}
            className='w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50'
          />
          <label className='flex items-center gap-2 text-sm text-tres-text-primary'>
            <input
              type='checkbox'
              checked={showTagline}
              onChange={(e) => setDraftShow(e.target.checked)}
              disabled={busy || branding.isLoading}
              className='h-4 w-4 accent-tres-action'
            />
            Show the tagline
          </label>
        </div>
        <div className='mt-4'>
          <button
            type='button'
            onClick={() => void saveWelcome()}
            disabled={busy || !dirty}
            className={buttonVariants({ variant: 'primary', size: 'lg' })}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      <div className='mt-4 rounded-md border border-border bg-background p-4'>
        <div className='text-sm font-medium text-tres-text-primary'>Logo</div>
        <p className='mt-1 text-xs text-tres-text-secondary'>
          PNG or JPEG, up to 512 KB. Shown centred above the title at up to 128
          pixels tall — a wide mark on a transparent background works best.
        </p>
        <div className='mt-3 flex flex-wrap items-center gap-4'>
          <div className='flex h-24 min-w-40 items-center justify-center rounded border border-dashed border-border bg-card px-4'>
            {logoSrc ? (
              <img src={logoSrc} alt='Current logo' className='max-h-20 w-auto max-w-xs' />
            ) : (
              <span className='text-xs text-tres-text-secondary'>
                No logo — the TReS logo is shown
              </span>
            )}
          </div>
          <div className='flex flex-col gap-2 text-xs text-tres-text-secondary'>
            {logo && (
              <span>
                {logo.mime} · {Math.round(logo.bytes / 1024)} KB · uploaded{' '}
                {logo.updated_at}
              </span>
            )}
            <div className='flex gap-2'>
              <input
                ref={fileInput}
                type='file'
                accept='image/png,image/jpeg'
                className='hidden'
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void upload(f);
                }}
              />
              <button
                type='button'
                onClick={() => fileInput.current?.click()}
                disabled={busy}
                className={buttonVariants({ variant: 'secondary', size: 'lg' })}
              >
                {logo ? 'Change' : 'Upload'}
              </button>
              {logo && (
                <button
                  type='button'
                  onClick={() => void removeLogo()}
                  disabled={busy}
                  className={buttonVariants({ variant: 'secondary', size: 'lg' })}
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
